import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Alert, Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { familyStaffApi } from './parentApi';

export default function FamilyStaffInbox({ channel = 'teacher' }) {
  const isRm = channel === 'rm';
  const qc = useQueryClient();
  const listQ = useQuery(
    ['familyStaffInbox', channel],
    async () => ((isRm ? await familyStaffApi.listRm() : await familyStaffApi.listTeacher()).data?.data) || []
  );
  const [activeId, setActiveId] = useState(null);
  const threadQ = useQuery(
    ['familyStaffThread', channel, activeId],
    async () => {
      const res = isRm ? await familyStaffApi.getRm(activeId) : await familyStaffApi.getTeacher(activeId);
      return res.data?.data;
    },
    { enabled: !!activeId }
  );
  const [body, setBody] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setErr('');
    if (!body.trim() || !activeId) return;
    setSaving(true);
    try {
      if (isRm) await familyStaffApi.replyRm(activeId, body);
      else await familyStaffApi.replyTeacher(activeId, body);
      setBody('');
      qc.invalidateQueries(['familyStaffThread', channel, activeId]);
      qc.invalidateQueries(['familyStaffInbox', channel]);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send.');
    } finally {
      setSaving(false);
    }
  };

  const rows = listQ.data || [];
  const t = threadQ.data;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
      <Paper sx={{ p: 2, minHeight: 360 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{isRm ? 'Family messages' : 'Family messages'}</Typography>
        {listQ.isLoading && <CircularProgress size={22} />}
        {rows.map((r) => (
          <Box
            key={r.threadId}
            onClick={() => setActiveId(r.threadId)}
            sx={{
              py: 1, borderBottom: '1px solid #eee', cursor: 'pointer',
              bgcolor: activeId === r.threadId ? 'action.hover' : 'transparent',
            }}
          >
            <Typography fontWeight={700}>{r.studentName}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {r.overdue ? 'Overdue · ' : ''}{r.lastPreview}
            </Typography>
          </Box>
        ))}
        {!listQ.isLoading && rows.length === 0 && (
          <Typography color="text.secondary">No family messages yet.</Typography>
        )}
      </Paper>
      <Paper sx={{ p: 2 }}>
        {!activeId && <Typography color="text.secondary">Select a conversation.</Typography>}
        {threadQ.isLoading && <CircularProgress />}
        {t && (
          <>
            <Typography variant="h6">{t.studentName || t.subject}</Typography>
            <Typography variant="caption" color="text.secondary">{t.priorityLabel || t.threadType}</Typography>
            <Box sx={{ my: 2 }}>
              {(t.messages || []).map((m) => (
                <Box key={m.messageId} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {m.senderKind === 'parent' ? 'Family' : 'You'}
                  </Typography>
                  <Typography>{m.body}</Typography>
                </Box>
              ))}
            </Box>
            {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
            <TextField fullWidth multiline minRows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply" />
            <Button sx={{ mt: 1 }} variant="contained" onClick={send} disabled={saving}>Send reply</Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
